import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useEffect
} from "react";
import { I_RealestateCard } from "../../model/interface/realestate-card.interface";
import RealEstateCardInfo from "./RealEstateEditModalAlert";
import BtnSimple from "../../components/buttons/BtbSimple";
import styled from "@emotion/styled";
import BadgeCustom from "../../components/badge/BadgeCustom";
import RealestateShareMultiple from "./RealestateShareMultiple";
import { useLocation } from "react-router-dom";

export interface RefererContext {
  values: I_RealestateCard[];
  allSelectedIds: number[];
  addProperty: (property: I_RealestateCard) => void;
  setValues?: React.Dispatch<React.SetStateAction<I_RealestateCard[]>>;
  removeProperty: (property: I_RealestateCard) => void;
  clearProperties: () => void;
  floatMode: boolean;
  setFloatMode: React.Dispatch<React.SetStateAction<boolean>>;
  refBottom: React.RefObject<HTMLButtonElement>;
}

const RefererPropertiesInstances = createContext<RefererContext | null>(
  null
);

interface IRefererPropertiesProps {
  children: any;
  floatMode?: any;
  values?: I_RealestateCard[];
}

const RefererPropertiesContext = forwardRef(
  (
    props: IRefererPropertiesProps,
    ref: React.Ref<RefererContext | null>
  ) => {
    const [values, setValues] = useState<I_RealestateCard[]>(
      props?.values ?? []
    );
    const [floatMode, setFloatMode] = useState<boolean>(
      props?.floatMode ?? true
    );
    const refBottom = React.useRef<HTMLButtonElement>(null);

    const addProperty = useCallback((property: I_RealestateCard) => {
      setValues(prev => [...prev, property]);
    }, []);

    const removeProperty = useCallback((property: I_RealestateCard) => {
      setValues(prev => prev.filter(item => item.id !== property.id));
    }, []);

    const clearProperties = useCallback(() => {
      setValues([]);
      setFloatMode(true);
    }, []);

    const allSelectedIds = useMemo(() => {
      return values.map(item => item.id);
    }, [values]);

    const callBackDelete = useCallback((item: I_RealestateCard) => {
      removeProperty(item);
    }, []);

    const location = useLocation();

    const showNetworks = useMemo(() => {
      return (
        values.every(item => item.status.id === 0) &&
        location.pathname === "/realestates"
      );
    }, [location.pathname, values]);

    useEffect(() => {}, []);

    const contextValue: RefererContext = {
      values,
      allSelectedIds,
      addProperty,
      removeProperty,
      clearProperties,
      floatMode,
      setFloatMode,
      refBottom
    };

    // for use outside of the context, with ref
    useImperativeHandle(ref, () => {
      return contextValue;
    }, [contextValue]);

    return (
      <RefererPropertiesInstances.Provider value={contextValue}>
        {props.children}
        <Content show={floatMode ? values.length > 0 : true}>
          {showNetworks && (
            <div>
              <BadgeCustom
                badgeContent={floatMode ? allSelectedIds.length : 0}
              >
                <RealestateShareMultiple />
              </BadgeCustom>
            </div>
          )}

          <RealEstateCardInfo
            id="modal-referer-properties"
            title="Referir Propiedades"
            handleDelete={callBackDelete}
            refererContext={contextValue}
            btnToggle={
              <BadgeCustom
                badgeContent={floatMode ? allSelectedIds.length : 0}
              >
                <BtnSimple
                  id="btn-referer-properties"
                  ref={refBottom}
                  variant-custom={floatMode ? "blue-main" : "mute"}
                >
                  Referir propiedades
                </BtnSimple>
              </BadgeCustom>
            }
          />
        </Content>
      </RefererPropertiesInstances.Provider>
    );
  }
);

function useRefererProperties() {
  const context = useContext(RefererPropertiesInstances);
  if (!context) {
    return {
      values: [],
      allSelectedIds: [],
      addProperty: () => {},
      removeProperty: () => {},
      clearProperties: () => {},
      floatMode: true,
      setFloatMode: () => {},
      refBottom: null
    };
  }
  return context;
}

export { RefererPropertiesContext, useRefererProperties };

const Content = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: 50px;
  right: 50px;
  z-index: 1200;
  transition: transform 0.3s ease-out;
  transform: ${({ show }) =>
    show ? "translateY(0)" : "translateY(500%)"};
  display: flex;
  gap: 30px;
`;
